import type {
  ChatMessage,
  Consultation,
  HealthRecord,
  Medication,
  Provider,
  Report,
  Task,
} from './types'

export type DashboardTabId =
  | 'home'
  | 'chat'
  | 'timeline'
  | 'health'
  | 'sleep'
  | 'fitness'
  | 'nutrition'
  | 'symptoms'
  | 'longevity'
  | 'tests'
  | 'records'
  | 'medications'
  | 'supplements'
  | 'immunizations'
  | 'reports'
  | 'genetics'
  | 'goals'
  | 'settings'
  | 'consult'
  | 'providers'
  | 'devices'
  | 'family'
  | 'emergency'

export type DashboardIconKey =
  | 'home'
  | 'chat'
  | 'timeline'
  | 'heart'
  | 'moon'
  | 'fire'
  | 'scale'
  | 'clipboard'
  | 'chart'
  | 'test'
  | 'folder'
  | 'medication'
  | 'supplement'
  | 'shield'
  | 'sparkles'
  | 'fingerprint'
  | 'flag'
  | 'settings'
  | 'video'
  | 'sync'
  | 'device'
  | 'users'
  | 'emergency'

export interface DashboardTab {
  iconKey: DashboardIconKey
  id: DashboardTabId
  label: string
}

export interface DashboardTabGroup {
  label: string | null
  tabs: DashboardTab[]
}

// ─── Tab definitions ─────────────────────────────────────────
export const DASHBOARD_TAB_GROUPS: DashboardTabGroup[] = [
  {
    label: null,
    tabs: [
      { id: 'home', label: 'Home', iconKey: 'home' },
      { id: 'chat', label: 'Chat', iconKey: 'chat' },
      { id: 'timeline', label: 'Timeline', iconKey: 'timeline' },
    ],
  },
  {
    label: 'Health',
    tabs: [
      { id: 'health', label: 'Health Metrics', iconKey: 'heart' },
      { id: 'sleep', label: 'Sleep', iconKey: 'moon' },
      { id: 'fitness', label: 'Fitness', iconKey: 'fire' },
      { id: 'nutrition', label: 'Nutrition', iconKey: 'scale' },
      { id: 'symptoms', label: 'Symptoms', iconKey: 'clipboard' },
      { id: 'longevity', label: 'Longevity', iconKey: 'chart' },
    ],
  },
  {
    label: 'Testing & Records',
    tabs: [
      { id: 'tests', label: 'Tests', iconKey: 'test' },
      { id: 'records', label: 'Records Vault', iconKey: 'folder' },
      { id: 'medications', label: 'Medications', iconKey: 'medication' },
      { id: 'supplements', label: 'Supplements', iconKey: 'supplement' },
      { id: 'immunizations', label: 'Immunizations', iconKey: 'shield' },
    ],
  },
  {
    label: 'Intelligence',
    tabs: [
      { id: 'reports', label: 'Reports', iconKey: 'sparkles' },
      { id: 'genetics', label: 'Genetics', iconKey: 'fingerprint' },
      { id: 'goals', label: 'Goals', iconKey: 'flag' },
      { id: 'settings', label: 'Settings', iconKey: 'settings' },
    ],
  },
  {
    label: 'Care & Connections',
    tabs: [
      { id: 'consult', label: 'Consultations', iconKey: 'video' },
      { id: 'providers', label: 'Providers', iconKey: 'sync' },
      { id: 'devices', label: 'Wearables', iconKey: 'device' },
      { id: 'family', label: 'Family', iconKey: 'users' },
      { id: 'emergency', label: 'Emergency', iconKey: 'emergency' },
    ],
  },
]

// Flat list derived from groups (for backward compat)
export const DASHBOARD_TABS: DashboardTab[] = DASHBOARD_TAB_GROUPS.flatMap((group) => group.tabs)

export const DASHBOARD_DEFAULT_ACTIVE_TAB: DashboardTabId = 'home'
export const DEFAULT_UPLOAD_SOURCE = 'PDF Upload'
export const DEFAULT_UPLOAD_KIND = 'Visit Note'
export const DEFAULT_REPORT_FOCUS = 'Cardiometabolic'
export const DEFAULT_OPENROUTER_MODEL = 'anthropic/claude-sonnet-4.6'
export const PROVIDER_DISCONNECTED_LABEL = 'Not connected'

// ─── AI Chat ─────────────────────────────────────────────────
export const INITIAL_CHAT_MESSAGES: ChatMessage[] = []

export const QUICK_PROMPTS = [
  { id: 'qp-labs', label: 'Summarize my labs' },
  { id: 'qp-plan', label: 'Review care plan' },
  { id: 'qp-ecg', label: 'ECG analysis' },
  { id: 'qp-trends', label: 'Compare 6-month trends' },
  { id: 'qp-upload', label: 'Upload a record' },
]

export const MOCK_AI_RESPONSES = {
  lab: 'Your most recent CBC + CMP panel from Quest Diagnostics was parsed successfully. Key highlights:\n\n• **ApoB**: 82 mg/dL (down from 93 — within optimal range)\n• **LDL-C**: 98 mg/dL (borderline, trending down)\n• **Fasting glucose**: 91 mg/dL (normal)\n• **Hemoglobin A1c**: 5.4% (optimal)\n\nOverall metabolic markers are improving. I recommend retesting in 8 weeks to confirm the trend.',
  plan: 'Here are your active care-plan tasks:\n\n1. **Upload specialist follow-up note** — Due this week\n2. **Share latest report with PCP** — Due tomorrow\n3. **Confirm medication list after dosage change** — Due today\n\n✓ Retest lipid panel — Completed\n\nWould you like me to help with any of these?',
  ecg: 'Your most recent 12-lead resting ECG shows:\n\n• **Rhythm**: Normal sinus rhythm\n• **Heart rate**: 72 bpm\n• **PR interval**: 160 ms (normal)\n• **QRS duration**: 88 ms (normal)\n• **QTc**: 410 ms (normal)\n\nNo ST changes, no arrhythmias detected. Everything looks healthy.',
  trend: 'Your 6-month health trend score has improved steadily:\n\n• Sep: 66 → Oct: 68 → Nov: 71 → Dec: 73 → Jan: 78 → **Feb: 82**\n\nThat\'s a 24% improvement over 6 months. Key drivers: improved lipid markers, consistent sleep patterns, and increased HRV. Keep it up!',
  upload: 'To upload a new record, head over to the **Records Vault** tab. You can:\n\n1. Choose your PDF\n2. Click "Upload and parse"\n3. Alberto will auto-detect title, source, and category\n\nOptional advanced overrides are available before submit.',
  doctor: 'You can schedule a consultation from the **Consultations** tab. Currently available:\n\n• **Dr. Alex Kim** (Internal Medicine) — 10:30 AM today\n• **Dr. Priya Patel** (Cardiology) — 2:00 PM today\n• **Dr. Michael Chen** (Endocrinology) — Tomorrow 9:00 AM\n\nWould you like me to take you there?',
  default:
    'Let me look through your health records to help with that. Based on your current profile, you have 3 connected providers, 6 parsed records, and 2 reports generated. Is there a specific area you\'d like me to focus on?',
}

// ─── Health metrics ──────────────────────────────────────────
export const VITAL_SIGNS = [
  { id: 'vs-hr', label: 'Heart Rate', value: '72', unit: 'bpm', trend: 'stable', color: 'rose' },
  { id: 'vs-bp', label: 'Blood Pressure', value: '118/76', unit: 'mmHg', trend: 'improving', color: 'sky' },
  { id: 'vs-spo2', label: 'SpO2', value: '98', unit: '%', trend: 'stable', color: 'emerald' },
  { id: 'vs-hrv', label: 'HRV', value: '42', unit: 'ms', trend: 'improving', color: 'violet' },
]

export const VITALS_HISTORY = [
  { id: 'vh-1', date: 'Feb 22, 2026', heartRate: 72, bp: '118/76', spo2: 98, hrv: 42 },
  { id: 'vh-2', date: 'Feb 15, 2026', heartRate: 75, bp: '122/78', spo2: 97, hrv: 38 },
  { id: 'vh-3', date: 'Feb 8, 2026', heartRate: 70, bp: '116/74', spo2: 99, hrv: 45 },
  { id: 'vh-4', date: 'Feb 1, 2026', heartRate: 74, bp: '120/77', spo2: 98, hrv: 40 },
]

export const TREND_SERIES = [
  { label: 'Sep', value: 66 },
  { label: 'Oct', value: 68 },
  { label: 'Nov', value: 71 },
  { label: 'Dec', value: 73 },
  { label: 'Jan', value: 78 },
  { label: 'Feb', value: 82 },
]

// ─── Records vault ───────────────────────────────────────────
export const VAULT_CATEGORIES = ['All', 'Lab', 'Imaging', 'Visit Note', 'Medication'] as const

export const VAULT_STATS = { totalFiles: 12, totalSize: '24.3 MB', categories: 4 }

export const INITIAL_RECORDS: HealthRecord[] = [
  {
    id: 'record-cbc',
    title: 'CBC + CMP panel',
    source: 'Quest Diagnostics',
    kind: 'Lab',
    status: 'Parsed',
    updatedAt: '5m ago',
    fileSizeBytes: 250_880,
    parseError: null,
    parseSummary: 'CBC and CMP values were extracted. Most markers are in reference range with improving lipid trend.',
    textPreview: 'CBC + CMP panel. ApoB 82 mg/dL, LDL-C 98 mg/dL, fasting glucose 91 mg/dL, HbA1c 5.4%.',
  },
  {
    id: 'record-mri',
    title: 'MRI lumbar spine',
    source: 'Provider Portal',
    kind: 'Imaging',
    status: 'Parsed',
    updatedAt: '18m ago',
    fileSizeBytes: 13_002_342,
    parseError: null,
    parseSummary: 'Imaging report indicates no acute findings and mild disc desiccation at L4-L5.',
    textPreview: 'MRI lumbar spine impression: no significant spinal canal stenosis. Mild disc desiccation at L4-L5.',
  },
  {
    id: 'record-visit',
    title: 'Primary care follow-up',
    source: 'PDF Upload',
    kind: 'Visit Note',
    status: 'Parsed',
    updatedAt: '38m ago',
    fileSizeBytes: 911_360,
    parseError: null,
    parseSummary: 'Follow-up visit note parsed with routine wellness recommendations and no urgent issues.',
    textPreview: 'Primary care follow-up visit. Vitals stable. Continue current plan and schedule routine follow-up.',
  },
  {
    id: 'record-ecg',
    title: 'ECG 12-lead resting',
    source: 'Quest Diagnostics',
    kind: 'Lab',
    status: 'Parsed',
    updatedAt: '2h ago',
    fileSizeBytes: 1_258_291,
    parseError: null,
    parseSummary: 'ECG interpretation shows normal sinus rhythm and no acute ischemic changes.',
    textPreview: '12-lead ECG: normal sinus rhythm, HR 72 bpm, PR 160 ms, QRS 88 ms, QTc 410 ms.',
  },
  {
    id: 'record-rx',
    title: 'Atorvastatin 20mg Rx',
    source: 'Provider Portal',
    kind: 'Medication',
    status: 'Parsed',
    updatedAt: '3d ago',
    fileSizeBytes: 122_880,
    parseError: null,
    parseSummary: 'Medication record parsed for atorvastatin 20mg daily with active refill details.',
    textPreview: 'Medication: Atorvastatin 20 mg tablet. Instructions: take once daily. Refills available.',
  },
  {
    id: 'record-xray',
    title: 'Chest X-ray PA/Lateral',
    source: 'Imaging Center Export',
    kind: 'Imaging',
    status: 'Parsed',
    updatedAt: '1w ago',
    fileSizeBytes: 9_122_611,
    parseError: null,
    parseSummary: 'Chest X-ray report parsed with no acute cardiopulmonary abnormalities noted.',
    textPreview: 'Chest X-ray PA/Lateral: heart size normal, lungs clear, no focal consolidation or pleural effusion.',
  },
]

// ─── Providers ───────────────────────────────────────────────
export const INITIAL_PROVIDERS: Provider[] = [
  { id: 'epic', name: 'Epic (Northwell)', type: 'EHR', connected: true, lastSync: '2m ago' },
  { id: 'cerner', name: 'Cerner (City Medical)', type: 'EHR', connected: true, lastSync: '6m ago' },
  { id: 'quest', name: 'Quest Diagnostics', type: 'Lab', connected: true, lastSync: '1m ago' },
  { id: 'mychart', name: 'MyChart Upload Inbox', type: 'Documents', connected: false, lastSync: 'Not connected' },
]

// ─── Reports ─────────────────────────────────────────────────
export const INITIAL_REPORTS: Report[] = [
  {
    id: 'report-metabolic',
    title: 'Metabolic risk summary',
    focus: 'Cardiometabolic',
    confidence: 92,
    summary: 'ApoB and LDL are down versus baseline. Continue current nutrition and movement plan.',
    createdAt: '8m ago',
    createdAtIso: '2026-02-22T09:16:00.000Z',
    includesAiAnalysis: true,
    narrative: {
      headline: 'Cardiometabolic trend remains favorable with improving lipid trajectory.',
      findings: [
        { detail: 'ApoB and LDL markers are moving in the right direction compared with baseline.', tone: 'positive' },
        { detail: 'Glucose-related markers remain in normal range, supporting stable metabolic control.', tone: 'positive' },
        { detail: 'Continue monitoring to confirm that improvement persists over the next cycle.', tone: 'neutral' },
      ],
      actions: [
        { detail: 'Recheck lipid panel and fasting glucose in 8 weeks.', priority: 'high' },
        { detail: 'Maintain current nutrition and activity routines.', priority: 'medium' },
        { detail: 'Share this report with your PCP at next follow-up.', priority: 'low' },
      ],
      sourceCoverage: ['CBC + CMP panel (Quest Diagnostics)', 'Primary care follow-up (PDF Upload)'],
      priorContext: ['Compared with prior cardiometabolic report trend in this workspace.'],
    },
  },
  {
    id: 'report-hormone',
    title: 'Hormone balance report',
    focus: 'Hormones',
    confidence: 88,
    summary: 'Ferritin is improving but thyroid antibodies should be rechecked during your next cycle.',
    createdAt: '27m ago',
    createdAtIso: '2026-02-03T13:30:00.000Z',
    includesAiAnalysis: true,
    narrative: {
      headline: 'Hormone profile is broadly stable with one follow-up signal to watch.',
      findings: [
        { detail: 'Ferritin trend appears improved versus the previous cycle.', tone: 'positive' },
        { detail: 'Thyroid antibody monitoring remains important at the next interval.', tone: 'watch' },
        { detail: 'Current pattern supports routine follow-up rather than urgent escalation.', tone: 'neutral' },
      ],
      actions: [
        { detail: 'Repeat thyroid panel at the next planned cycle.', priority: 'high' },
        { detail: 'Track symptom changes alongside lab timing for cleaner comparisons.', priority: 'medium' },
        { detail: 'Review results with endocrinology consult if trend worsens.', priority: 'low' },
      ],
      sourceCoverage: ['Hormone panel records (Quest Diagnostics)', 'Previous hormone report context'],
      priorContext: ['Incorporates prior report history from this workspace.'],
    },
  },
]

export const REPORT_TEMPLATES = {
  All: {
    title: 'Comprehensive health report',
    summary:
      'Multi-domain analysis combining cardiometabolic, hormone, inflammation, and recovery signals for a full view.',
  },
  Cardiometabolic: {
    title: 'Cardiometabolic trend report',
    summary: 'Lipids and fasting glucose are moving in the right direction. Keep current plan and retest in 8 weeks.',
  },
  Hormones: {
    title: 'Hormone and thyroid report',
    summary: 'Hormone markers are mostly stable. Follow up on antibody trend and discuss timing with your clinician.',
  },
  Inflammation: {
    title: 'Inflammation and immune report',
    summary: 'hs-CRP is mildly elevated but trending down from prior cycle. Monitor recovery and sleep consistency.',
  },
  Recovery: {
    title: 'Recovery and stress report',
    summary: 'Sleep and HRV indicators suggest improving resilience. Continue current protocol and hydration targets.',
  },
}

// ─── Care plan tasks ─────────────────────────────────────────
export const INITIAL_TASKS: Task[] = [
  { id: 'task-upload-note', title: 'Upload specialist follow-up note', due: 'Due this week', done: false },
  { id: 'task-share-report', title: 'Share latest report with PCP', due: 'Due tomorrow', done: false },
  { id: 'task-retest', title: 'Retest lipid panel', due: 'Due in 8 weeks', done: true },
  { id: 'task-med-list', title: 'Confirm medication list after dosage change', due: 'Due today', done: false },
]

// ─── Consultations ───────────────────────────────────────────
export const AVAILABLE_DOCTORS = [
  { id: 'doc-kim', name: 'Dr. Alex Kim', specialty: 'Internal Medicine', available: true, nextSlot: '10:30 AM today' },
  { id: 'doc-patel', name: 'Dr. Priya Patel', specialty: 'Cardiology', available: true, nextSlot: '2:00 PM today' },
  { id: 'doc-chen', name: 'Dr. Michael Chen', specialty: 'Endocrinology', available: false, nextSlot: 'Tomorrow 9:00 AM' },
]

export const CONSULTATION_REASONS = ['Follow-up', 'New concern', 'Lab review', 'Medication review', 'Second opinion']

export const INITIAL_CONSULTATIONS: Consultation[] = [
  { id: 'consult-1', doctorId: 'doc-kim', doctorName: 'Dr. Alex Kim', date: 'Feb 25, 2026', time: '10:30 AM', joinUrl: 'https://meet.albertohealth.demo/consult-1', reason: 'Lab review', status: 'upcoming' },
  { id: 'consult-2', doctorId: 'doc-patel', doctorName: 'Dr. Priya Patel', date: 'Feb 10, 2026', time: '3:00 PM', reason: 'Follow-up', status: 'completed', notes: 'Discussed lipid panel results. Continue current statin dosage.' },
]

// ─── Timeline ────────────────────────────────────────────────
export const TIMELINE_EVENTS = [
  { id: 'tl-1', type: 'lab', title: 'CBC + CMP panel parsed', source: 'Quest Diagnostics', date: 'Feb 22, 2026', time: '9:14 AM', detail: 'All markers within normal range. ApoB trending down 12%.' },
  { id: 'tl-2', type: 'ai', title: 'Report generated', source: 'Alberto Health', date: 'Feb 22, 2026', time: '9:16 AM', detail: 'Metabolic risk summary — 92% confidence.' },
  { id: 'tl-3', type: 'wearable', title: 'Wearable data sync', source: 'Wearable Hub', date: 'Feb 21, 2026', time: '11:30 PM', detail: 'Sleep 7h 42m · HRV 44ms · Resting HR 68 bpm.' },
  { id: 'tl-4', type: 'medication', title: 'Medication refill confirmed', source: 'Walgreens', date: 'Feb 20, 2026', time: '2:45 PM', detail: 'Atorvastatin 20mg — 90-day supply picked up.' },
  { id: 'tl-5', type: 'appointment', title: 'Consultation with Dr. Patel', source: 'Alberto Health', date: 'Feb 10, 2026', time: '3:00 PM', detail: 'Follow-up on lipid panel. Continue current statin dosage.' },
  { id: 'tl-6', type: 'record', title: 'MRI lumbar spine uploaded', source: 'Provider Portal', date: 'Feb 8, 2026', time: '10:22 AM', detail: 'No significant findings. Mild disc desiccation at L4-L5.' },
  { id: 'tl-7', type: 'lab', title: 'Hormone panel parsed', source: 'Quest Diagnostics', date: 'Feb 5, 2026', time: '8:50 AM', detail: 'Thyroid markers stable. Ferritin improving — 68 ng/mL.' },
  { id: 'tl-8', type: 'wearable', title: 'Recovery ring sync', source: 'Recovery Ring', date: 'Feb 4, 2026', time: '7:00 AM', detail: 'Readiness 82 · Sleep score 78 · Activity score 71.' },
  { id: 'tl-9', type: 'ai', title: 'Hormone balance report', source: 'Alberto Health', date: 'Feb 3, 2026', time: '1:30 PM', detail: 'Ferritin improving. Recheck thyroid antibodies next cycle.' },
  { id: 'tl-10', type: 'record', title: 'Primary care follow-up note', source: 'Epic (Northwell)', date: 'Jan 28, 2026', time: '4:15 PM', detail: 'Annual wellness visit. All vitals normal. Next visit in 6 months.' },
]

// ─── Medications ─────────────────────────────────────────────
export const INITIAL_MEDICATIONS: Medication[] = [
  { id: 'med-1', name: 'Atorvastatin', dosage: '20 mg', frequency: 'Once daily', timeOfDay: 'Evening', prescriber: 'Dr. Alex Kim', startDate: 'Sep 15, 2025', refillDate: 'May 20, 2026', status: 'active', notes: 'Take with food. Monitor lipid panel every 3 months.' },
  { id: 'med-2', name: 'Vitamin D3', dosage: '5,000 IU', frequency: 'Once daily', timeOfDay: 'Morning', prescriber: 'Dr. Priya Patel', startDate: 'Nov 1, 2025', refillDate: 'Apr 1, 2026', status: 'active', notes: 'Supplement for low vitamin D levels (was 22 ng/mL).' },
  { id: 'med-3', name: 'Omega-3 Fish Oil', dosage: '2,000 mg', frequency: 'Once daily', timeOfDay: 'Morning', prescriber: 'Self', startDate: 'Jan 10, 2026', refillDate: 'Mar 15, 2026', status: 'active', notes: 'EPA/DHA for cardiovascular support.' },
  { id: 'med-4', name: 'Lisinopril', dosage: '10 mg', frequency: 'Once daily', timeOfDay: 'Morning', prescriber: 'Dr. Alex Kim', startDate: 'Jun 1, 2025', refillDate: 'Mar 1, 2026', status: 'active', notes: 'Blood pressure management. Check renal function annually.' },
  { id: 'med-5', name: 'Metformin', dosage: '500 mg', frequency: 'Twice daily', timeOfDay: 'Morning & evening', prescriber: 'Dr. Michael Chen', startDate: 'Mar 1, 2025', refillDate: 'N/A', status: 'discontinued', notes: 'Discontinued Oct 2025 — A1c normalized to 5.4%.' },
]

// ─── Tests ──────────────────────────────────────────────────
export const TEST_PARTNERS = [
  { id: 'fh', name: 'Function Health', tagline: '100+ lab biomarkers', color: 'indigo' },
  { id: 'quest', name: 'Quest Diagnostics', tagline: 'Standard & specialty labs', color: 'sky' },
  { id: 'labcorp', name: 'Labcorp', tagline: 'Nationwide lab network', color: 'emerald' },
]

export const TEST_CATEGORIES = ['All', 'Blood Panel', 'Hormones', 'Heart', 'Metabolic', 'Immune', 'Nutrients']

export const TEST_CATALOG = [
  { id: 'test-fh-100', name: 'Function Health 100+', description: 'Comprehensive panel — 100+ biomarkers including lipids, metabolic, hormones, inflammation, and nutrients.', partner: 'Function Health', category: 'Blood Panel', price: 499, popular: true },
  { id: 'test-apob', name: 'ApoB & Lipid Panel', description: 'Advanced cardiovascular risk — ApoB, LDL-P, LDL-C, HDL, triglycerides, and Lp(a).', partner: 'Quest Diagnostics', category: 'Heart', price: 89, popular: true },
  { id: 'test-cmp', name: 'Complete Metabolic Panel', description: 'Glucose, electrolytes, kidney and liver function — 14 key metabolic markers.', partner: 'Labcorp', category: 'Metabolic', price: 39, popular: false },
  { id: 'test-cbc', name: 'CBC with Differential', description: 'White blood cells, red blood cells, hemoglobin, hematocrit, and platelet count.', partner: 'Quest Diagnostics', category: 'Blood Panel', price: 29, popular: false },
  { id: 'test-thyroid', name: 'Thyroid Complete Panel', description: 'TSH, Free T3, Free T4, Reverse T3, and thyroid antibodies (TPO, TG).', partner: 'Labcorp', category: 'Hormones', price: 129, popular: true },
  { id: 'test-hormone-m', name: 'Male Hormone Panel', description: 'Total & free testosterone, SHBG, estradiol, DHEA-S, and cortisol.', partner: 'Function Health', category: 'Hormones', price: 199, popular: false },
  { id: 'test-hormone-f', name: 'Female Hormone Panel', description: 'Estradiol, progesterone, FSH, LH, SHBG, DHEA-S, and cortisol.', partner: 'Function Health', category: 'Hormones', price: 199, popular: false },
  { id: 'test-hscrp', name: 'hs-CRP & Inflammation', description: 'High-sensitivity C-reactive protein, ESR, and homocysteine.', partner: 'Quest Diagnostics', category: 'Immune', price: 59, popular: false },
  { id: 'test-a1c', name: 'Hemoglobin A1c', description: 'Average blood sugar over 2–3 months — key diabetes screening marker.', partner: 'Labcorp', category: 'Metabolic', price: 29, popular: false },
  { id: 'test-vitd', name: 'Vitamin D, B12 & Folate', description: '25-hydroxy vitamin D, B12, and folate — essential nutrient levels.', partner: 'Quest Diagnostics', category: 'Nutrients', price: 69, popular: true },
  { id: 'test-iron', name: 'Iron & Ferritin Panel', description: 'Serum iron, ferritin, TIBC, and transferrin saturation.', partner: 'Labcorp', category: 'Nutrients', price: 49, popular: false },
  { id: 'test-cardiac', name: 'Cardiac Risk Panel', description: 'BNP, troponin, CK-MB, and advanced lipid markers for heart health.', partner: 'Function Health', category: 'Heart', price: 149, popular: false },
  { id: 'test-immune', name: 'Immune Function Panel', description: 'Lymphocyte subsets, immunoglobulins (IgA, IgG, IgM), and complement levels.', partner: 'Labcorp', category: 'Immune', price: 179, popular: false },
  { id: 'test-omega', name: 'Omega-3 & Fatty Acids', description: 'Omega-3 index, EPA, DHA, omega-6:3 ratio for cardiovascular optimization.', partner: 'Quest Diagnostics', category: 'Nutrients', price: 99, popular: false },
]

// ─── Goals & Action Plans ───────────────────────────────────
export const INITIAL_GOALS = [
  { id: 'goal-apob', title: 'Get ApoB below 80 mg/dL', category: 'Cardiovascular', target: 80, current: 82, unit: 'mg/dL', direction: 'lower', startDate: 'Sep 2025', deadline: 'Jun 2026', status: 'in_progress', progress: 78 },
  { id: 'goal-sleep', title: 'Average 7+ hours sleep', category: 'Sleep', target: 7, current: 6.8, unit: 'hrs', direction: 'higher', startDate: 'Jan 2026', deadline: 'Apr 2026', status: 'in_progress', progress: 60 },
  { id: 'goal-weight', title: 'Reach 175 lbs', category: 'Body Composition', target: 175, current: 182, unit: 'lbs', direction: 'lower', startDate: 'Nov 2025', deadline: 'May 2026', status: 'in_progress', progress: 45 },
  { id: 'goal-vitd', title: 'Vitamin D above 50 ng/mL', category: 'Nutrients', target: 50, current: 48, unit: 'ng/mL', direction: 'higher', startDate: 'Nov 2025', deadline: 'Mar 2026', status: 'in_progress', progress: 90 },
  { id: 'goal-zone2', title: '150 min Zone 2 cardio/week', category: 'Fitness', target: 150, current: 120, unit: 'min', direction: 'higher', startDate: 'Jan 2026', deadline: 'Ongoing', status: 'in_progress', progress: 80 },
  { id: 'goal-a1c', title: 'Maintain A1c under 5.5%', category: 'Metabolic', target: 5.5, current: 5.4, unit: '%', direction: 'lower', startDate: 'Mar 2025', deadline: 'Ongoing', status: 'achieved', progress: 100 },
]

export const GOAL_ACTIONS = [
  { id: 'action-1', goalId: 'goal-apob', text: 'Continue atorvastatin 20mg daily', done: true },
  { id: 'action-2', goalId: 'goal-apob', text: 'Limit saturated fat to <13g/day', done: false },
  { id: 'action-3', goalId: 'goal-apob', text: 'Add 2g/day plant sterols', done: false },
  { id: 'action-4', goalId: 'goal-sleep', text: 'No screens after 10 PM', done: true },
  { id: 'action-5', goalId: 'goal-sleep', text: 'Bedroom temp at 67°F', done: true },
  { id: 'action-6', goalId: 'goal-sleep', text: 'Take magnesium glycinate before bed', done: false },
  { id: 'action-7', goalId: 'goal-zone2', text: '30 min walk/jog 5x per week', done: false },
  { id: 'action-8', goalId: 'goal-zone2', text: 'Keep HR between 120–140 bpm', done: true },
]

// ─── Supplements & Protocols ────────────────────────────────
export const INITIAL_SUPPLEMENTS = [
  { id: 'supp-1', name: 'Magnesium Glycinate', dosage: '400 mg', timing: 'Before bed', brand: 'Thorne', protocol: 'Sleep optimization', startDate: 'Jan 2026', status: 'active', notes: 'Supports sleep quality and muscle recovery.' },
  { id: 'supp-2', name: 'Vitamin D3 + K2', dosage: '5,000 IU / 100 mcg', timing: 'Morning with fat', brand: 'Thorne', protocol: 'Bone & immune', startDate: 'Nov 2025', status: 'active', notes: 'K2 ensures calcium goes to bones, not arteries.' },
  { id: 'supp-3', name: 'Omega-3 (EPA/DHA)', dosage: '2,000 mg', timing: 'Morning', brand: 'Nordic Naturals', protocol: 'Cardiovascular', startDate: 'Jan 2026', status: 'active', notes: 'Triglyceride form for better absorption.' },
  { id: 'supp-4', name: 'Creatine Monohydrate', dosage: '5 g', timing: 'Post-workout', brand: 'Thorne', protocol: 'Performance', startDate: 'Dec 2025', status: 'active', notes: 'Supports muscle and cognitive function.' },
  { id: 'supp-5', name: 'Berberine', dosage: '500 mg', timing: 'Before meals', brand: 'Thorne', protocol: 'Metabolic', startDate: 'Oct 2025', status: 'paused', notes: 'Paused after A1c normalized. May resume if glucose drifts.' },
  { id: 'supp-6', name: 'NAC (N-Acetyl Cysteine)', dosage: '600 mg', timing: 'Morning', brand: 'Life Extension', protocol: 'Antioxidant', startDate: 'Feb 2026', status: 'active', notes: 'Glutathione precursor. Liver and lung support.' },
]

export const SUPPLEMENT_PROTOCOLS = [
  { id: 'proto-1', name: 'Sleep Optimization', supplements: ['supp-1'], goal: 'Improve deep sleep and reduce wake-ups', startDate: 'Jan 2026', biomarkerTarget: 'HRV > 45ms' },
  { id: 'proto-2', name: 'Cardiovascular Support', supplements: ['supp-3'], goal: 'Lower ApoB, support endothelial function', startDate: 'Jan 2026', biomarkerTarget: 'ApoB < 80 mg/dL' },
  { id: 'proto-3', name: 'Metabolic Health', supplements: ['supp-5'], goal: 'Maintain fasting glucose and A1c', startDate: 'Oct 2025', biomarkerTarget: 'A1c < 5.5%' },
]

// ─── Longevity & Biological Age ─────────────────────────────
export const LONGEVITY_DATA = {
  biologicalAge: 34,
  chronologicalAge: 38,
  ageDelta: -4,
  healthspanScore: 82,
  rateOfAging: 0.92,
  lastAssessment: 'Feb 22, 2026',
}

export const HEALTHSPAN_CATEGORIES = [
  { id: 'hs-cardio', label: 'Cardiovascular', score: 88, status: 'optimal', icon: 'heart' },
  { id: 'hs-metabolic', label: 'Metabolic', score: 79, status: 'good', icon: 'bolt' },
  { id: 'hs-immune', label: 'Immune', score: 84, status: 'optimal', icon: 'shield' },
  { id: 'hs-neuro', label: 'Neurological', score: 76, status: 'good', icon: 'brain' },
  { id: 'hs-hormonal', label: 'Hormonal', score: 81, status: 'optimal', icon: 'spark' },
  { id: 'hs-musculo', label: 'Musculoskeletal', score: 73, status: 'fair', icon: 'bone' },
]

export const BIOAGE_HISTORY = [
  { date: 'Sep 2025', bioAge: 37, chronAge: 38 },
  { date: 'Oct 2025', bioAge: 36.5, chronAge: 38 },
  { date: 'Nov 2025', bioAge: 36, chronAge: 38 },
  { date: 'Dec 2025', bioAge: 35.2, chronAge: 38 },
  { date: 'Jan 2026', bioAge: 34.5, chronAge: 38 },
  { date: 'Feb 2026', bioAge: 34, chronAge: 38 },
]

export const OPTIMAL_ZONES = [
  { marker: 'ApoB', current: 82, optimal: [50, 80], normal: [0, 130], unit: 'mg/dL', status: 'near-optimal' },
  { marker: 'Fasting Glucose', current: 91, optimal: [70, 90], normal: [70, 100], unit: 'mg/dL', status: 'normal' },
  { marker: 'hs-CRP', current: 0.8, optimal: [0, 1.0], normal: [0, 3.0], unit: 'mg/L', status: 'optimal' },
  { marker: 'HbA1c', current: 5.4, optimal: [4.5, 5.4], normal: [4.0, 5.7], unit: '%', status: 'optimal' },
  { marker: 'Vitamin D', current: 48, optimal: [40, 60], normal: [30, 100], unit: 'ng/mL', status: 'optimal' },
  { marker: 'Homocysteine', current: 9.2, optimal: [5, 9], normal: [5, 15], unit: 'µmol/L', status: 'near-optimal' },
]

// ─── Sleep ──────────────────────────────────────────────────
export const SLEEP_SUMMARY = {
  avgDuration: '7h 12m',
  avgScore: 78,
  avgDeep: '1h 24m',
  avgRem: '1h 48m',
  avgLatency: '12 min',
  avgWakeUps: 1.3,
  consistency: 82,
}

export const SLEEP_HISTORY = [
  { id: 'sl-1', date: 'Feb 22', duration: 7.7, score: 82, deep: 1.5, rem: 1.9, light: 3.8, awake: 0.5, bedtime: '10:45 PM', wakeTime: '6:28 AM' },
  { id: 'sl-2', date: 'Feb 21', duration: 6.9, score: 71, deep: 1.1, rem: 1.6, light: 3.7, awake: 0.5, bedtime: '11:30 PM', wakeTime: '6:24 AM' },
  { id: 'sl-3', date: 'Feb 20', duration: 7.5, score: 80, deep: 1.4, rem: 2.0, light: 3.6, awake: 0.5, bedtime: '10:30 PM', wakeTime: '6:00 AM' },
  { id: 'sl-4', date: 'Feb 19', duration: 7.2, score: 76, deep: 1.3, rem: 1.7, light: 3.8, awake: 0.4, bedtime: '10:50 PM', wakeTime: '6:02 AM' },
  { id: 'sl-5', date: 'Feb 18', duration: 6.5, score: 65, deep: 0.9, rem: 1.4, light: 3.6, awake: 0.6, bedtime: '11:45 PM', wakeTime: '6:15 AM' },
  { id: 'sl-6', date: 'Feb 17', duration: 7.8, score: 85, deep: 1.6, rem: 2.1, light: 3.5, awake: 0.6, bedtime: '10:15 PM', wakeTime: '6:03 AM' },
  { id: 'sl-7', date: 'Feb 16', duration: 7.3, score: 79, deep: 1.4, rem: 1.8, light: 3.6, awake: 0.5, bedtime: '10:40 PM', wakeTime: '5:58 AM' },
]

// ─── Fitness & Activity ─────────────────────────────────────
export const FITNESS_SUMMARY = {
  weeklyActiveMinutes: 245,
  weeklyZone2Minutes: 120,
  avgDailySteps: 9420,
  vo2Max: 42.5,
  restingHR: 62,
  fitnessAge: 33,
}

export const RECENT_WORKOUTS = [
  { id: 'wk-1', type: 'Run', title: 'Zone 2 easy run', duration: '42 min', calories: 380, avgHR: 132, date: 'Feb 22', distance: '4.2 mi' },
  { id: 'wk-2', type: 'Strength', title: 'Upper body pull', duration: '55 min', calories: 290, avgHR: 118, date: 'Feb 21', distance: null },
  { id: 'wk-3', type: 'Walk', title: 'Morning walk', duration: '35 min', calories: 180, avgHR: 98, date: 'Feb 21', distance: '1.8 mi' },
  { id: 'wk-4', type: 'Run', title: 'Tempo intervals', duration: '38 min', calories: 420, avgHR: 155, date: 'Feb 20', distance: '3.8 mi' },
  { id: 'wk-5', type: 'Strength', title: 'Lower body', duration: '50 min', calories: 310, avgHR: 122, date: 'Feb 19', distance: null },
  { id: 'wk-6', type: 'Cycling', title: 'Stationary bike Z2', duration: '45 min', calories: 350, avgHR: 128, date: 'Feb 18', distance: '14 mi' },
]

export const WEEKLY_ACTIVITY = [
  { day: 'Mon', steps: 10240, activeMin: 42 },
  { day: 'Tue', steps: 8930, activeMin: 55 },
  { day: 'Wed', steps: 12100, activeMin: 35 },
  { day: 'Thu', steps: 7650, activeMin: 50 },
  { day: 'Fri', steps: 9800, activeMin: 38 },
  { day: 'Sat', steps: 11200, activeMin: 45 },
  { day: 'Sun', steps: 6020, activeMin: 20 },
]

// ─── Nutrition ──────────────────────────────────────────────
export const NUTRITION_SUMMARY = {
  avgCalories: 2150,
  calorieTarget: 2200,
  avgProtein: 145,
  avgCarbs: 210,
  avgFat: 78,
  proteinTarget: 150,
  carbTarget: 220,
  fatTarget: 73,
  hydration: 2.8,
  hydrationTarget: 3.0,
}

export const TODAYS_MEALS = [
  { id: 'meal-1', name: 'Breakfast', time: '7:30 AM', items: 'Greek yogurt, blueberries, granola, black coffee', calories: 420, protein: 28, carbs: 52, fat: 12 },
  { id: 'meal-2', name: 'Lunch', time: '12:15 PM', items: 'Grilled chicken salad, olive oil dressing, whole grain bread', calories: 580, protein: 42, carbs: 38, fat: 26 },
  { id: 'meal-3', name: 'Snack', time: '3:30 PM', items: 'Fruit, almond butter, protein shake', calories: 340, protein: 30, carbs: 32, fat: 14 },
  { id: 'meal-4', name: 'Dinner', time: '7:00 PM', items: 'Salmon, roasted vegetables, brown rice', calories: 650, protein: 45, carbs: 58, fat: 24 },
]

export const NUTRITION_WEEK = [
  { day: 'Mon', calories: 2080, protein: 138, carbs: 200, fat: 75 },
  { day: 'Tue', calories: 2250, protein: 152, carbs: 218, fat: 80 },
  { day: 'Wed', calories: 1980, protein: 140, carbs: 195, fat: 70 },
  { day: 'Thu', calories: 2300, protein: 148, carbs: 230, fat: 82 },
  { day: 'Fri', calories: 2150, protein: 145, carbs: 210, fat: 78 },
  { day: 'Sat', calories: 2400, protein: 135, carbs: 260, fat: 85 },
  { day: 'Sun', calories: 1900, protein: 130, carbs: 188, fat: 68 },
]

// ─── Wearables & Devices ───────────────────────────────────
export const CONNECTED_DEVICES = [
  { id: 'dev-1', name: 'Smartwatch Pro 2', type: 'Smartwatch', brand: 'Wearable Labs', connected: true, lastSync: '2 min ago', battery: 78, metrics: ['Heart Rate', 'ECG', 'SpO2', 'Steps', 'Sleep'] },
  { id: 'dev-2', name: 'Recovery Ring Gen 3', type: 'Ring', brand: 'Ring Labs', connected: true, lastSync: '15 min ago', battery: 62, metrics: ['HRV', 'Sleep Stages', 'Body Temp', 'Readiness'] },
  { id: 'dev-3', name: 'Body Composition Scale', type: 'Scale', brand: 'Home Health', connected: true, lastSync: '6h ago', battery: null, metrics: ['Weight', 'Body Fat %', 'Muscle Mass', 'BMI'] },
  { id: 'dev-4', name: 'Glucose Sensor G7', type: 'CGM', brand: 'Sensor Health', connected: false, lastSync: 'Not connected', battery: null, metrics: ['Glucose', 'Time in Range', 'Glucose Variability'] },
  { id: 'dev-5', name: 'Blood Pressure Monitor', type: 'BP Monitor', brand: 'Pulse Labs', connected: true, lastSync: '1d ago', battery: null, metrics: ['Blood Pressure', 'Irregular Heartbeat'] },
  { id: 'dev-6', name: 'Sleep Pod 3', type: 'Mattress', brand: 'Sleep Labs', connected: false, lastSync: 'Not connected', battery: null, metrics: ['Sleep Tracking', 'Bed Temperature', 'HRV'] },
]

export const CGM_DATA = [
  { time: '6:00 AM', glucose: 88 },
  { time: '7:00 AM', glucose: 92 },
  { time: '8:00 AM', glucose: 118 },
  { time: '9:00 AM', glucose: 105 },
  { time: '10:00 AM', glucose: 94 },
  { time: '11:00 AM', glucose: 90 },
  { time: '12:00 PM', glucose: 96 },
  { time: '1:00 PM', glucose: 132 },
  { time: '2:00 PM', glucose: 115 },
  { time: '3:00 PM', glucose: 98 },
  { time: '4:00 PM', glucose: 91 },
  { time: '5:00 PM', glucose: 88 },
]

// ─── Genetics ───────────────────────────────────────────────
export const GENETIC_PROFILE = {
  provider: '23andMe + Whole Genome Sequencing',
  dateProcessed: 'Aug 2025',
  totalVariants: 842,
  actionable: 12,
}

export const GENETIC_RISKS = [
  { id: 'gene-1', variant: 'APOE ε3/ε4', gene: 'APOE', category: 'Cardiovascular', risk: 'elevated', description: 'One copy of ε4 allele — moderately elevated Alzheimer\'s and cardiovascular risk.', actionable: true, recommendation: 'Prioritize ApoB optimization, exercise, and sleep.' },
  { id: 'gene-2', variant: 'MTHFR C677T', gene: 'MTHFR', category: 'Metabolic', risk: 'moderate', description: 'Heterozygous — may reduce folate metabolism efficiency by ~35%.', actionable: true, recommendation: 'Consider methylfolate instead of folic acid. Monitor homocysteine.' },
  { id: 'gene-3', variant: 'CYP2D6 *1/*4', gene: 'CYP2D6', category: 'Pharmacogenomics', risk: 'info', description: 'Intermediate metabolizer — some medications may need dosage adjustment.', actionable: true, recommendation: 'Share with prescribing physicians. Affects codeine, SSRIs, beta-blockers.' },
  { id: 'gene-4', variant: 'FTO rs9939609', gene: 'FTO', category: 'Metabolic', risk: 'moderate', description: 'Associated with higher BMI tendency and increased appetite signaling.', actionable: false, recommendation: 'Focus on protein-first meals and regular exercise.' },
  { id: 'gene-5', variant: 'CLOCK rs1801260', gene: 'CLOCK', category: 'Sleep', risk: 'low', description: 'Minor variant affecting circadian rhythm — slight evening chronotype tendency.', actionable: false, recommendation: 'Maintain consistent sleep schedule. Morning light exposure.' },
  { id: 'gene-6', variant: 'BRCA1/2', gene: 'BRCA', category: 'Oncology', risk: 'normal', description: 'No pathogenic variants detected in BRCA1 or BRCA2.', actionable: false, recommendation: 'Standard age-appropriate cancer screening.' },
]

export const PHARMACOGENOMICS = [
  { drug: 'Atorvastatin', gene: 'SLCO1B1', status: 'Normal metabolizer', note: 'Standard dosing appropriate' },
  { drug: 'Clopidogrel', gene: 'CYP2C19', status: 'Normal metabolizer', note: 'Expected efficacy' },
  { drug: 'Codeine', gene: 'CYP2D6', status: 'Intermediate metabolizer', note: 'Reduced conversion — may need alternative' },
  { drug: 'Metoprolol', gene: 'CYP2D6', status: 'Intermediate metabolizer', note: 'May accumulate — consider lower dose' },
  { drug: 'Warfarin', gene: 'CYP2C9/VKORC1', status: 'Normal sensitivity', note: 'Standard dosing range' },
]

// ─── Symptoms ───────────────────────────────────────────────
export const SYMPTOM_CATEGORIES = ['Energy', 'Mood', 'Digestion', 'Pain', 'Sleep Quality', 'Stress']

export const DAILY_CHECKINS = [
  { id: 'ci-1', date: 'Feb 22', energy: 8, mood: 7, digestion: 9, pain: 1, sleepQuality: 8, stress: 3, notes: 'Good day overall. Morning run helped energy.' },
  { id: 'ci-2', date: 'Feb 21', energy: 6, mood: 6, digestion: 7, pain: 2, sleepQuality: 6, stress: 5, notes: 'Poor sleep last night, felt sluggish.' },
  { id: 'ci-3', date: 'Feb 20', energy: 7, mood: 8, digestion: 8, pain: 1, sleepQuality: 8, stress: 3, notes: '' },
  { id: 'ci-4', date: 'Feb 19', energy: 7, mood: 7, digestion: 8, pain: 1, sleepQuality: 7, stress: 4, notes: 'Mild headache in afternoon.' },
  { id: 'ci-5', date: 'Feb 18', energy: 5, mood: 5, digestion: 6, pain: 3, sleepQuality: 5, stress: 7, notes: 'Stressful work day. Lower back tightness.' },
  { id: 'ci-6', date: 'Feb 17', energy: 8, mood: 8, digestion: 9, pain: 1, sleepQuality: 9, stress: 2, notes: 'Best sleep in weeks. Felt great.' },
  { id: 'ci-7', date: 'Feb 16', energy: 7, mood: 7, digestion: 7, pain: 2, sleepQuality: 7, stress: 4, notes: '' },
]

export const SYMPTOM_LOG = [
  { id: 'sym-1', date: 'Feb 19', symptom: 'Headache', severity: 3, duration: '2 hours', trigger: 'Dehydration', notes: 'Resolved after water and rest.' },
  { id: 'sym-2', date: 'Feb 18', symptom: 'Lower back tightness', severity: 4, duration: 'All day', trigger: 'Sitting at desk', notes: 'Improved with stretching.' },
  { id: 'sym-3', date: 'Feb 14', symptom: 'Bloating', severity: 3, duration: '3 hours', trigger: 'Dairy', notes: 'After cheese at dinner.' },
  { id: 'sym-4', date: 'Feb 10', symptom: 'Brain fog', severity: 5, duration: '4 hours', trigger: 'Poor sleep', notes: 'Only 5h sleep night before.' },
]

// ─── Immunizations ──────────────────────────────────────────
export const IMMUNIZATION_RECORDS = [
  { id: 'vax-1', name: 'COVID-19 (Pfizer)', date: 'Oct 15, 2025', lot: 'FK4832', provider: 'CVS Pharmacy', status: 'up-to-date', nextDue: 'Oct 2026' },
  { id: 'vax-2', name: 'Influenza', date: 'Sep 20, 2025', lot: 'QK3821', provider: 'CVS Pharmacy', status: 'up-to-date', nextDue: 'Sep 2026' },
  { id: 'vax-3', name: 'Tdap', date: 'Mar 10, 2022', lot: 'TD2910', provider: 'Dr. Alex Kim', status: 'up-to-date', nextDue: 'Mar 2032' },
  { id: 'vax-4', name: 'Hepatitis B (3-dose)', date: 'Jun 2018', lot: '—', provider: 'City Medical', status: 'complete', nextDue: 'N/A' },
  { id: 'vax-5', name: 'MMR', date: 'Childhood', lot: '—', provider: '—', status: 'complete', nextDue: 'N/A' },
  { id: 'vax-6', name: 'Shingles (Shingrix)', date: '—', lot: '—', provider: '—', status: 'recommended', nextDue: 'Age 50+' },
]

export const SCREENING_SCHEDULE = [
  { id: 'scr-1', name: 'Colonoscopy', frequency: 'Every 10 years', lastDone: 'N/A', nextDue: 'Age 45 (2033)', status: 'not-yet-due' },
  { id: 'scr-2', name: 'Skin check (dermatology)', frequency: 'Annual', lastDone: 'Aug 2025', nextDue: 'Aug 2026', status: 'up-to-date' },
  { id: 'scr-3', name: 'Eye exam', frequency: 'Every 2 years', lastDone: 'Jan 2025', nextDue: 'Jan 2027', status: 'up-to-date' },
  { id: 'scr-4', name: 'Dental cleaning', frequency: 'Every 6 months', lastDone: 'Nov 2025', nextDue: 'May 2026', status: 'up-to-date' },
  { id: 'scr-5', name: 'Blood pressure check', frequency: 'Annual', lastDone: 'Feb 2026', nextDue: 'Feb 2027', status: 'up-to-date' },
  { id: 'scr-6', name: 'Lipid panel', frequency: 'Every 3 months', lastDone: 'Feb 2026', nextDue: 'May 2026', status: 'up-to-date' },
]

// ─── Emergency & Safety ─────────────────────────────────────
export const EMERGENCY_PROFILE = {
  bloodType: 'O+',
  organDonor: true,
  allergies: ['Penicillin (rash)', 'Shellfish (anaphylaxis)'],
  conditions: ['Hypertension (controlled)', 'Hyperlipidemia'],
  currentMedications: ['Atorvastatin 20mg', 'Lisinopril 10mg', 'Vitamin D3 5000IU'],
  emergencyContacts: [
    { name: 'Maria Johnson', relationship: 'Spouse', phone: '(555) 234-5678' },
    { name: 'Robert Johnson', relationship: 'Brother', phone: '(555) 345-6789' },
  ],
  primaryPhysician: 'Dr. Alex Kim — Northwell Health',
  insuranceProvider: 'Blue Cross Blue Shield — PPO',
  memberId: 'BCBS-882-441-9920',
  advanceDirective: true,
  advanceDirectiveDate: 'Jan 2024',
}

// ─── Family Health ──────────────────────────────────────────
export const FAMILY_MEMBERS = [
  { id: 'fam-1', name: 'Maria Johnson', relationship: 'Spouse', age: 36, sharedAccess: true, conditions: ['Hypothyroidism'], medications: 2, lastCheckup: 'Jan 2026' },
  { id: 'fam-2', name: 'Ethan Johnson', relationship: 'Son', age: 8, sharedAccess: true, conditions: [], medications: 0, lastCheckup: 'Dec 2025' },
  { id: 'fam-3', name: 'Olivia Johnson', relationship: 'Daughter', age: 5, sharedAccess: true, conditions: ['Seasonal allergies'], medications: 1, lastCheckup: 'Nov 2025' },
]

export const FAMILY_HISTORY = [
  { id: 'fhx-1', condition: 'Type 2 Diabetes', relative: 'Father', age: 55, notes: 'Managed with metformin' },
  { id: 'fhx-2', condition: 'Hypertension', relative: 'Father', age: 48, notes: 'On ACE inhibitor' },
  { id: 'fhx-3', condition: 'Breast cancer', relative: 'Maternal grandmother', age: 62, notes: 'Survived, treated with surgery' },
  { id: 'fhx-4', condition: 'Heart disease', relative: 'Paternal grandfather', age: 70, notes: 'MI at age 70, deceased age 78' },
  { id: 'fhx-5', condition: 'Hypothyroidism', relative: 'Mother', age: 42, notes: 'Hashimoto\'s, on levothyroxine' },
  { id: 'fhx-6', condition: 'Colorectal cancer', relative: 'Paternal uncle', age: 58, notes: 'Treated, in remission' },
]
